import PriceRangeFilter from "./pricerangefilter.js";
import SubSearchFilter from "./subsearchfilter.js";
import ProductQuantityFilter from "./productquantityfilter.js";
import ColorSelectorFilter from "./colorselectorfilter.js";
import FilterMenu from "./filtermenu.js";
import UpdateFilterUrl from "./updatefilterurl.js";
import FilterValues from "./filtervalues.js";
import { prodListValues } from './shared.js';

const clearAllFilters = document.getElementById('clearAllFilters');
const clearAllFiltersMobile = document.getElementById('clearAllFiltersMobile');
const list = document.getElementById('selectedFilterList');
const listMobile = document.getElementById('selectedFilterListMobile');
const filtersSelected = document.getElementById('filtersSelected');
const filtersSelectedMobile = document.getElementById('filtersSelectedMobile');
const viewMore = document.getElementById('ViewMoreSelectedFilters');

// manages area that displays which filters have been selected
class SelectedFilters {
    constructor(){
        this.listAccordions = null;
    }

    /**
     * Gets the list element that contains the selected filters.
     * @return {HTMLElement} The list element that contains the selected filters.
     */
    get selectedFilterList() { return list; }


    init(accordions){
        this.listAccordions = accordions;
        this.events();
    }
    /**
     * Adds a filter to the selected filter group.
     * @param {string} id The ID of the filter.
     * @param {string} label The label of the filter.
     * @return {void}
     */
    addToGroup(id, label) {
        let newDiv = this.createDiv('selected_' + id, label);

        if (listMobile.firstElementChild !== null) {
            viewMore.classList.remove('hide');
            if (viewMore.getAttribute('aria-expanded') === 'false') {
                listMobile.firstElementChild.classList.add('hide');
                listMobile.firstElementChild.classList.remove('displayFlex');
            }
        }

        if (listMobile.textContent.trim() === 'Please select a filter') {
            listMobile.textContent = '';
        }

       this.prepend(list, newDiv);

        // need to recreate div to append to other filter box
       newDiv = this.createDiv('selected_' + id, label);

        this.prepend(listMobile, newDiv);

        filtersSelected.classList.remove('hide');
        filtersSelectedMobile.classList.remove('hide');
        this.showClearAll(); // note: moving focus to top of page is disorienting for a11y users
    };

    /**
     * Prepends a child element to a list element.
     * @param {HTMLElement} list The list element.
     * @param {HTMLElement} child The child element to prepend.
     * @return {void}
     */
    prepend(list, child) {
        if (list.hasChildNodes()) {
            list.insertBefore(child, list.firstChild);
        } else {
            list.appendChild(child);
        }
    };

    /**
     * Creates a new div element for a selected filter.
     * @param {string} newId The ID of the new div.
     * @param {string} filterName The name of the filter.
     * @return {HTMLElement} The created div element.
     */
    createDiv(newId, filterName) {
        const newDiv = document.createElement('div');
        newDiv.classList.add(newId, 'selectedFilter', 'displayFlex', 'flexAlignItemsCenter', 'flexSpaceBetween', 'padding10', 'text18');

        const p = document.createElement('p');
        p.className = 'c-xs-quarter-3 noPadding';
        p.setAttribute('role', 'alert');
        p.setAttribute('aria-atomic', 'true');
        p.innerHTML = '<span class="visually-hidden">Now Showing </span>';
        p.append(filterName); // string → text node, XSS-safe

        const button = document.createElement('button');
        button.className = `noLine darkestLink btnToHyp padding5 ${newId}`;
        button.innerHTML = '<svg class="show" height="20px" width="20px" role="img"><title></title><use href="/icons/iconLibrary.svg#svgIcon-remove"></use></svg>';
        button.querySelector('svg').setAttribute('aria-label', `remove ${filterName} filter`);
        button.querySelector('title').textContent = `Remove ${filterName} filter`;

        newDiv.appendChild(p);
        newDiv.appendChild(button);

        button.addEventListener('click', () => this.removeFilterClick(newId));
        button.addEventListener('keypress', (e) => this.removeFilterKeypress(e, newId));
        return newDiv;
    };

    /**
     * Handles the click event to remove a selected filter.
     * @param {string} element The ID of the selected filter element.
     * @return {boolean} Always returns false.
     */
    removeFilterClick(element) {
        let elementId = element.replace('selected_', '');

        if (elementId === 'PriceRange') {
            PriceRangeFilter.clear();
        } else if (elementId === 'txtInnerSearch') {
            SubSearchFilter.clear();
        } else if (elementId === 'txtProductQuantity') {
            ProductQuantityFilter.clear();
        } else if (FilterValues.isColorId(elementId)) {
            ColorSelectorFilter.remove(document.getElementById(elementId));
        } else {
            FilterMenu.markUnSelected(document.getElementById(elementId));
        }

        UpdateFilterUrl.update(FilterValues, prodListValues, false);

        return false;
    };

    /**
     * Handles the keypress event to remove a selected filter.
     * @param {KeyboardEvent} e The keyboard event.
     * @param {string} element The ID of the selected filter element.
     * @return {void}
     */
    removeFilterKeypress(e, element) {
        if (e.key === 'Enter') {
            this.removeFilterClick(element);
        }
    };

    /**
     * Adds a filter to the selected filter group.
     * @param {HTMLElement} element The filter element.
     * @param {string} filterName The name of the filter.
     * @return {void}
     */
    addToSelectedFilter(element, filterName) {
        if (filterName) {
            this.addToGroup(element.id, filterName);
        } else if (element.id.indexOf('ImprintLocation') !== -1) {
            this.addToGroup(element.id, document.getElementById('ImprintLocationCountFilterTitle').value.replace('##IMPLOC##', element.getAttribute('data-filter')));
        } else if (element.id.indexOf('ImprintColor') !== -1) {
            this.addToGroup(element.id, document.getElementById('ImprintColorCountFilterTitle').value.replace('##IMPCOLOR##', element.getAttribute('data-filter')));
        } else {
            let label = element.getAttribute('data-filter');
            this.addToGroup(element.id, label);
        }
    };

    /**
     * Resets the view more filter to its default state.
     * @return {void}
     */
    resetViewMoreFilter() {
        let label = viewMore.innerHTML;
        if (viewMore.getAttribute('aria-expanded') === 'true') {
            viewMore.setAttribute('aria-expanded', 'false');
            label = "View All Selected";
            viewMore.innerHTML = label;
            viewMore.setAttribute("data-text", label);
        } else {
            viewMore.setAttribute('aria-expanded', 'true');
            label = "View Fewer Filters";
            viewMore.innerHTML = label;
            viewMore.setAttribute("data-text", label);
        }

        viewMore.classList.add('hide');
    };

    /**
     * Removes a filter from the selected filter group.
     * @param {HTMLElement} element The filter element to remove.
     * @return {void}
     */
    removeFromSelectedFilter(element) {
        // remove the div in selected filters
        let filterMobile = listMobile.querySelector('.selected_' + element.id);
        let filter = list.querySelector('.selected_' + element.id);

        if (filterMobile !== null) {
            if (listMobile.children.length > 1) {
                list.removeChild(filter);
                listMobile.removeChild(filterMobile);

                listMobile.firstElementChild.classList.remove('hide');
                listMobile.firstElementChild.classList.add('displayFlex');

                if (listMobile.children.length === 1) {
                    this.resetViewMoreFilter();
                }
            } else {
                this.removeAllFromSelectedFilter();
            }
        }
    };

    /**
     * Removes all filters from the selected filter group.
     * @return {void}
     */
    removeAllFromSelectedFilter() {
        list.innerHTML = '';
        listMobile.innerHTML = 'Please select a filter';
        this.resetViewMoreFilter();
        this.hideClearAll();
        if (this.listAccordions) {
            this.listAccordions.close(document.getElementById("selectedContentMobile"));
        }
    };

    /**
     * Toggles the view more and less selected filters visibility and CSS to change the display.
     * @param {Event} e The event object.
     * @return {void}
     */    
    toggleSelectedFiltersView(e) {
        e.preventDefault();

        this.toggleSelectedFilterItems();
        let label = viewMore.innerHTML;
        if (viewMore.getAttribute('aria-expanded') === 'true') {
            viewMore.setAttribute('aria-expanded', 'false');
            label = "View All Selected";
            viewMore.innerHTML = label;
            viewMore.setAttribute("data-text", label);
        } else {
            viewMore.setAttribute('aria-expanded', 'true');
            label = "View Fewer Filters";
            viewMore.innerHTML = label;
            viewMore.setAttribute("data-text", label);
        }
    };

    /**
     * Toggles the visibility of the selected filter items in the mobile view.
     * @return {void}
     */
    toggleSelectedFilterItems() {
        for (let i = 1; i < listMobile.children.length; i++) {
            let item = listMobile.children[i];
            if (item.classList.contains("hide")) {
                item.classList.remove("hide");
            } else {
                item.classList.add("hide");
            }
            if (item.classList.contains("hide")) {
                item.classList.remove("displayFlex");
            } else {
                item.classList.add("displayFlex");
            }
        }
    };

    /**
     * Attaches event listeners to the filter elements.
     * @return {void}
     */
    events() {
        clearAllFilters.addEventListener('click', (e) => {
            e.preventDefault();

            FilterMenu.unSelectAllFilters(true);
            this.hideClearAll();
        });

        clearAllFiltersMobile.addEventListener('click', (e) => {
            e.preventDefault();

            FilterMenu.unSelectAllFilters(true);
            this.hideClearAll();
            if (this.listAccordions) {
                this.listAccordions.close(document.getElementById("selectedContentMobile"));
            }
        });

        viewMore.addEventListener('click', this.toggleSelectedFiltersView.bind(this));

        const selectedFiltersMobile = document.getElementById('selectedFiltersMobile');
        if (selectedFiltersMobile) {
            const controlledId = selectedFiltersMobile.getAttribute('aria-controls');
            const accordionContent = controlledId ? document.getElementById(controlledId) : listMobile;

            selectedFiltersMobile.addEventListener('click', () => {
                const isExpanded = selectedFiltersMobile.getAttribute('aria-expanded') === 'true';
                selectedFiltersMobile.setAttribute('aria-expanded', String(!isExpanded));
                accordionContent.setAttribute('aria-hidden', String(isExpanded));
            });
        }
    };

    /**
     * Hides the "Clear All" button for filters.
     * @return {void}
     */
    hideClearAll() {
        clearAllFilters.classList.add('hide');
        clearAllFiltersMobile.classList.add('hide');
    };

    /**
     * Shows the "Clear All" button for filters.
     * @return {void}
     */
    showClearAll() {
        clearAllFilters.classList.remove('hide');
        clearAllFiltersMobile.classList.remove('hide');
    };
}

export default new SelectedFilters();